import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import modalStyles from "./ChooseVariantModal.module.css";
import { DoorDetail } from "../../Models/Models";
import WebService from "../../Services/WebService";
import { RootState } from "../../Services/Store";
import { useCart } from "../../Contexts/CartContext";
import { primaryProductImage } from "../../Services/productImages";
import { mediaUrl } from "../../Services/mediaUrl";
import { canAddToCart, stockStatusLabel } from "../../Services/stockAvailability";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import CrossButton from "../../Customs/Button/CrossButton";
import { fetchWoodQualities } from "../../Services/woodQualities";
const singleDoorIcon = "/Assets/singledoor.svg";
const doubleDoorIcon = "/Assets/doubledoor.svg";

interface MicaOption {
  mica_id: number;
  name: string;
  sku_id: string;
  image_url: string;
}

interface CartItemForEdit {
  cart_item_id: number;
  product_id: number;
  product_type: string;
  quantity: number;
  height: number;
  width: number;
  height_margin: number;
  width_margin: number;
  color: string;
  door_config: boolean;
  is_back_design: boolean;
  wood_quality_id: string;
  packaging_type_id: number;
  description: string;
  is_sagwan_bidding?: boolean;
  is_back_puc?: boolean;
  mica_id?:number;
  product?: DoorDetail;
   product_name?: string;
  product_description?: string;
  [key: string]: any;
}

interface WoodType {
  id: number;
  name: string;
}
interface PackagingOption {
  id: number;
  name: string;
  "price/sqft"?: number;
  price?: number;
}
type DoorSizeGroup = {
  height: string;
  width: string;
  marginHeight: string;
  marginWidth: string;
  quantity: number;
};

interface CustomerApi {
  customer_id: number;
  user_id: number;
  username: string;
  mobile_number: string;
  email?: string;
  company_name?: string;
}
interface CustomerOption {
  value: number;
  label: string;
  customer: CustomerApi;
}

interface Props {
  onClose: () => void;
  door?: DoorDetail;
  cartItem?: CartItemForEdit;
  onUpdated?: () => void;
  customerId?: number;
  itemType?: "PRODUCT" | "RAW_MATERIAL";
  saleOrderId?: number;
  userId?: number;
  isSaleOrderEdit?: boolean;
}

const initialGroup = (): DoorSizeGroup => ({
  height: "84in",
  width: "36in",
  marginHeight: "0.5in",
  marginWidth: "0.5in",
  quantity: 1,
});

// const hardCodedColors = [
//   "#FFD478",
//   "#845539",
//   "#b47e4c",
//   "#8d4c29",
//   "#712f21",
//   "#673020",
//   "#621C11",
// ];
   
const ChooseVariantModal: React.FC<Props> = ({
  onClose,
  door,
  cartItem,
  onUpdated,
  customerId: propCustomerId,
  itemType = "PRODUCT",
  saleOrderId,
  userId,
  isSaleOrderEdit = false,
}) => {
const navigate = useNavigate();
const { refreshCartCount } = useCart();
const user = useSelector((state: RootState) => state.auth.user);
const userType = user?.user_type;   
const userRole = (user?.role_name || "").toLowerCase();

const notifyCartUpdated = () => {
  refreshCartCount();
  if (onUpdated) setTimeout(onUpdated, 250);
};    

const isCustomer = userType === "CUSTOMER";
const isAdminOrEmployee = userType === "EMPLOYEE" || userType === "ADMIN";
// Allow add-to-existing-order only for ADMIN or accounts role
const canCustomizeExistingOrders = userType === "ADMIN" || userRole === "accounts";
 
const isRawMaterial = itemType === "RAW_MATERIAL";
const isProduct = itemType === "PRODUCT";
const isEditMode = !!cartItem;
const [customerQuery, setCustomerQuery] = useState("");
// const [customerPage, setCustomerPage] = useState(1);
// const [customerHasMore, setCustomerHasMore] = useState(false);
const [, setCustomerPickerOpen] = useState(false);
const customerPickerRef = useRef<HTMLDivElement | null>(null);
  // --- Common State
  const [posting, setPosting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [openCust, setOpenCust] = useState(false);
const custRef = useRef<HTMLDivElement | null>(null);

  // Product-only: sale order selection (dependent on chosen customer)
  const [saleOrders, setSaleOrders] = useState<number[]>([]);
  const [selectedSaleOrderId, setSelectedSaleOrderId] = useState<number | null>(null);

  // --- Raw Material State
  const [quantity, setQuantity] = useState<number>(cartItem?.quantity ?? 1);
  const [specialRequest, setSpecialRequest] = useState<string>(
    cartItem?.description ?? ""
  );
  const [extractedHeight, setExtractedHeight] = useState<number | null>(null);
  const [extractedWidth, setExtractedWidth] = useState<number | null>(null);

  const [woodTypeError, setWoodTypeError] = useState<string>("");
  const [packagingError, setPackagingError] = useState<string>("");
 
  // --- Product State
  const [sizeGroups, setSizeGroups] = useState<DoorSizeGroup[]>(
    isEditMode && cartItem && isProduct
      ? [
          {
            height: cartItem.height ? `${cartItem.height}in` : "84in",
            width: cartItem.width ? `${cartItem.width}in` : "36in",
            marginHeight: cartItem.height_margin
              ? `${cartItem.height_margin}in`
              : "0.5in",
            marginWidth: cartItem.width_margin
              ? `${cartItem.width_margin}in`
              : "0.5in",
            quantity: cartItem.quantity || 1,
          },
        ]
      : [initialGroup()]
  );
  const [woodTypes, setWoodTypes] = useState<WoodType[]>([]);
  const [selectedWood, setSelectedWood] = useState<string>(
    cartItem?.wood_quality_id ?? ""
  );
  const [packagingOptions, setPackagingOptions] = useState<PackagingOption[]>([]);
  const [selectedPackaging, setSelectedPackaging] = useState<string>(
    cartItem?.packaging_type_id?.toString() ?? ""
  );
  // const [selectedColor, setSelectedColor] = useState<string>(
  //   (cartItem &&
  //     Object.entries(colorCodeToName).find(
  //       ([code, name]) => name === cartItem.color
  //     )?.[0]) || hardCodedColors[0]
  // );

  
   const HEIGHT_MIN = 70;
const HEIGHT_MAX_NORMAL = 96;
const HEIGHT_MAX_HARDWOOD = 84;

const WIDTH_MIN = 24;
const WIDTH_MAX_NORMAL = 48;
const WIDTH_MAX_HARDWOOD = 44;

const parseInches = (v: string) => {
  const n = parseFloat(v.replace(/[^\d.]/g, ""));
  return Number.isNaN(n) ? 0 : n;
};
const toInches = (n: number) => `${n}in`;
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

// figure out selected wood & whether it's "Hardwood"
const selectedWoodObj = woodTypes.find(w => w.id === Number(selectedWood));
const isHardwood =
  !!selectedWoodObj &&
  selectedWoodObj.name.trim().toLowerCase().replace(/[\s-]+/g, "") === "hardwood";

// dynamic maxes based on wood type
const heightMax = isHardwood ? HEIGHT_MAX_HARDWOOD : HEIGHT_MAX_NORMAL;
const widthMax  = isHardwood ? WIDTH_MAX_HARDWOOD  : WIDTH_MAX_NORMAL;
 const mapCustomerPayload = (res: any): CustomerOption[] => {
  let arr: CustomerApi[] = [];
  if (res && res.data) {
    if (res.data.data && Array.isArray(res.data.data.users)) {
      arr = res.data.data.users;
    } else if (Array.isArray(res.data.users)) {
      arr = res.data.users;
    }
  }
  return arr.map((c: CustomerApi) => ({
    value: c.user_id,
   // in mapCustomerPayload:
    label: `${c.username}${c.company_name ? ` (${c.company_name})` : ""}${c.mobile_number ? ` - ${c.mobile_number}` : ""}`,
    customer: c,
  }));
};
useEffect(() => {
  function onDocClick(e: MouseEvent) {
    if (!customerPickerRef.current) return;
    if (!customerPickerRef.current.contains(e.target as Node)) {
      setCustomerPickerOpen(false);
    }
  }
  document.addEventListener("mousedown", onDocClick);
  return () => document.removeEventListener("mousedown", onDocClick);
}, []);

useEffect(() => {
  if (!isHardwood) return;
  setSizeGroups(prev =>
    prev.map(g => {
      const h = clamp(parseInches(g.height), HEIGHT_MIN, HEIGHT_MAX_HARDWOOD);
      const w = clamp(parseInches(g.width),  WIDTH_MIN,  WIDTH_MAX_HARDWOOD);
      return { ...g, height: toInches(h), width: toInches(w) };
    })
  );
}, [isHardwood, setSizeGroups]);

  const [doubleDoor, setDoubleDoor] = useState<boolean>(
    !!cartItem?.door_config
  );
  const [sagwanBidding, setSagwanBidding] = useState<boolean>(
    !!cartItem?.is_sagwan_bidding
  );
  const [backDesign, setBackDesign] = useState<boolean>(
    !!cartItem?.is_back_design
  );
  const [backPVC, setBackPVC] = useState<boolean>(!!cartItem?.is_back_puc);

  const [customerList, setCustomerList] = useState<CustomerOption[]>([]);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerOption | null>(null);
  const [customerLoading, setCustomerLoading] = useState(false);

  // Define chosenCustomerId for clarity and consistency
  const chosenCustomerId =
    propCustomerId && typeof propCustomerId === "number"
      ? propCustomerId
      : isCustomer
        ? user?.user_id
        : selectedCustomer?.customer?.user_id ?? null;

  const shouldShowCustomerDropdown =
    isAdminOrEmployee && !isEditMode && typeof propCustomerId !== "number";

  const modalRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, [onClose]);
  
  // Fetch editable sale orders for selected customer (product-only, role-gated)
  useEffect(() => {
    if (!isProduct) return;
    if (!canCustomizeExistingOrders || !chosenCustomerId) {
      setSaleOrders([]);
      setSelectedSaleOrderId(null);
      return;
    }
    WebService.getAPI<{ data: number[] }>({
      action: `/v1/door-management/saleorders/editable/user/${chosenCustomerId}`,
    })
      .then((res) => {
        const ids = Array.isArray(res?.data) ? res.data : [];
        setSaleOrders(ids.map((n) => Number(n)).filter((n) => !Number.isNaN(n)));
      })
      .catch(() => {
        setSaleOrders([]);
      });
  }, [isProduct, canCustomizeExistingOrders, chosenCustomerId]);
  const [micaPremium, setMicaPremium] = useState<MicaOption[]>([]);
const [micaStandard, setMicaStandard] = useState<MicaOption[]>([]);
const [selectedMicaId, setSelectedMicaId] = useState<number | null>(null);
const [hoveredMica, setHoveredMica] = useState<MicaOption | null>(null);
   
  
useEffect(() => {
  if(door) {
  WebService.getAPI<any>({ action: `/v1/door-management/mica-quality?name=${encodeURIComponent(door.name)}` })
    .then((res) => {
      const premium: MicaOption[] = res?.data?.premium || [];
      const standard: MicaOption[] = res?.data?.standard || [];
      setMicaPremium(premium);
      setMicaStandard(standard);
    })
    .catch(() => {
      toast.error("Failed to load mica options.");
      setMicaPremium([]);
      setMicaStandard([]);
    });
  }
}, [door]);
 useEffect(() => {
  if (cartItem?.mica_id != null) {
    const n = Number(cartItem.mica_id);
    setSelectedMicaId(Number.isNaN(n) ? null : n);
  }
}, [cartItem]);
useEffect(() => {
  if (selectedMicaId == null && cartItem?.mica_id != null) {
    const n = Number(cartItem.mica_id);
    if (!Number.isNaN(n)) setSelectedMicaId(n);
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [micaPremium, micaStandard]);
 useEffect(() => {
  // only in edit mode and if backend sent an id
  if (!cartItem?.mica_id) return;

  const id = Number(cartItem.mica_id);
  if (Number.isNaN(id)) return;

  // once lists are loaded, preselect
  const all = [...micaPremium, ...micaStandard];
  if (all.length === 0) return;       

  const exists = all.some(m => m.mica_id === id);
  if (exists) setSelectedMicaId(id);

}, [cartItem?.mica_id, micaPremium, micaStandard]);

  
useEffect(() => {
  if (!shouldShowCustomerDropdown) return;

  setCustomerLoading(true);
  const debounce = setTimeout(async () => {
    try {
      const params = new URLSearchParams({
        page_number: "1",
        limit: "20",
        status: "ACTIVE",
      });
      const q = customerQuery.trim();
      if (q) params.append("user_like", q);

      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/customers?${params.toString()}`,
      });

      const items = mapCustomerPayload(res);
      setCustomerList(items);
    } catch {
      setCustomerList([]);
    } finally {
      setCustomerLoading(false);
    }
  }, 250); 

  return () => clearTimeout(debounce);
}, [shouldShowCustomerDropdown, customerQuery]);

  // --- Raw Material raw-extraction
  useEffect(() => {
    if (isRawMaterial && door?.measuring_unit?.toLowerCase().includes("square feet")) {
      const name = door?.name || "";
      const match = name.match(/(\d+)\s*[*xX×]\s*(\d+)/);
      if (match) {
        setExtractedHeight(Number(match[1]));
        setExtractedWidth(Number(match[2]));
      } else {
        setExtractedHeight(null);
        setExtractedWidth(null);
      }
    } else {
      setExtractedHeight(null);
      setExtractedWidth(null);
    }
  }, [isRawMaterial, door]);
  useEffect(() => {
  const onDocClick = (e: MouseEvent) => {
    if (custRef.current && !custRef.current.contains(e.target as Node)) {
      setOpenCust(false);
    }
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") setOpenCust(false);
  };
  document.addEventListener("mousedown", onDocClick, true);
  document.addEventListener("keydown", onKey);
  return () => {
    document.removeEventListener("mousedown", onDocClick, true);
    document.removeEventListener("keydown", onKey);
  };
}, []);

  // --- Product: fetch wood and packaging options
  useEffect(() => {
    if (isProduct) {
      fetchWoodQualities()
        .then(setWoodTypes)
        .catch(() => setWoodTypes([]));

      WebService.getAPI<any>({ action: "/v1/door-management/users/packagings" })
        .then((res) => {
          const arr = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.data)
              ? res.data.data
              : [];
          setPackagingOptions(arr);
        })
        .catch(() => setPackagingOptions([]));
    }
  }, [isProduct]);

  const handleAddWoodQuality = () => {
    if (!isAdminOrEmployee) return;
    onClose();
    navigate("/inventory/wood-qualities");
  };
  
  // --- Product/Raw Material: handle add to cart/update
const handleConfirm = async () => {
  if (isProduct && !canAddToCart(door)) {
    toast.error(stockStatusLabel(door) || "This product is out of stock and cannot be added to the cart");
    return;
  }
  setPosting(true);
  setWoodTypeError("");
  setPackagingError("");

  // Derive product_type dynamically from data
  const productType = (cartItem?.product_type || door?.type || itemType) as "PRODUCT" | "RAW_MATERIAL";
    if (isRawMaterial && quantity < 1) {
    toast.error("Quantity must be at least 1.");
    setPosting(false);
    return;
  }
  if (isProduct && sizeGroups.some(g => !g.quantity || g.quantity < 1)) {
    toast.error("Quantity for all size groups must be at least 1.");
    setPosting(false);
    return;
  }

  let hasError = false;

  if (isProduct && !selectedWood) {
    setWoodTypeError("Please select a wood type.");
    hasError = true;
  }
  if (isProduct && !selectedPackaging) {
    setPackagingError("Please select a packaging option.");
    hasError = true;
  }
  if (hasError) {
    setPosting(false);
    return;
  }
    // --- RAW MATERIAL ---
if (isRawMaterial) {
  let payload: any = {
    product_id: door?.id || cartItem?.product_id,
    product_type: productType,
    quantity,
    description: specialRequest,
    product_name:
      cartItem?.product_name ||
      cartItem?.product?.product_name ||
      cartItem?.product?.name ||
      cartItem?.name ||
      door?.product_name ||
      door?.name ||
      "",
    product_description:
      cartItem?.product_description ||
      ("design_description" in (cartItem?.product || {}) && (cartItem?.product as any).design_description) ||
      cartItem?.product?.product_description ||
      cartItem?.product?.description ||
      cartItem?.description ||
      door?.product_description ||
      (("design_description" in (door || {})) ? (door as any).design_description : "") ||
      door?.description ||
      "",
  };
  if (extractedHeight !== null && extractedWidth !== null) {
    payload.height = extractedHeight;
    payload.width = extractedWidth;
  }

  // Backend recalculates sqft (with margins) and totals — do not send client totals.
  const rmUnitPrice = Number(
    (door as any)?.selling_price ??
      (door as any)?.retailer_selling_price ??
      cartItem?.unit_price ??
      (cartItem?.product as any)?.selling_price ??
      0
  );
  if (rmUnitPrice > 0) {
    payload.unit_price = rmUnitPrice;
  }
  delete payload.total_price;

  // Align payload with sale order item shape when editing an order
  if (isSaleOrderEdit && saleOrderId) {
    payload.sale_order_items_id = cartItem?.sale_order_items_id || cartItem?.cart_item_id;
    payload.sale_order_id = saleOrderId;
    payload.color = payload.color ?? cartItem?.color ?? "";
    payload.mica_id = payload.mica_id ?? cartItem?.mica_id;
    payload.is_sagwan_bidding = payload.is_sagwan_bidding ?? cartItem?.is_sagwan_bidding ?? false;
    payload.is_back_design = payload.is_back_design ?? cartItem?.is_back_design ?? false;
    payload.is_back_puc = payload.is_back_puc ?? cartItem?.is_back_puc ?? false;
    payload.wood_quality_id = payload.wood_quality_id ?? cartItem?.wood_quality_id;
    payload.packaging_type_id = payload.packaging_type_id ?? cartItem?.packaging_type_id;
    payload.discount = cartItem?.discount ?? 0;
    if (!payload.unit_price && cartItem?.unit_price) {
      payload.unit_price = cartItem.unit_price;
    }
  }

  try {
    if (isEditMode && cartItem?.cart_item_id) {
      // If coming from sale order edit flow, use the dedicated API endpoint
      if (isSaleOrderEdit && saleOrderId && userId) {
        const res = (await WebService.postAPI({
          action: `/v1/door-management/saleorders/${saleOrderId}/user/${userId}/item`,
          body: payload,
        })) as { status_code?: number; message?: string };
        if ([200, 201].includes(res.status_code ?? 0)) {
          toast.success(" Order Updated!");
          onClose();
          notifyCartUpdated();
        } else {
          toast.error(res?.message || "Could not update item.");
        }
      } else {
        // Regular cart update (PATCH endpoint)
        let actionUrl = `/v1/door-management/cart`;
        if (chosenCustomerId && chosenCustomerId !== user?.user_id) {
          actionUrl = `/v1/door-management/user/${chosenCustomerId}/cart`;
        }
        const res = (await WebService.patchAPI({
          action: actionUrl,
          body: { ...payload, cart_item_id: cartItem.cart_item_id },
        })) as { status_code?: number; message?: string };
        if ([200, 201].includes(res.status_code ?? 0)) {
          toast.success("Raw material updated in cart!");
          onClose();
          notifyCartUpdated();
        } else {
          toast.error(res?.message || "Could not update item.");
        }
      }
    } else {
      // Add mode POST
      // Check if an existing sale order is selected (for raw material as well)
      if (canCustomizeExistingOrders && selectedSaleOrderId && chosenCustomerId) {
        const res = (await WebService.postAPI({
          action: `/v1/door-management/saleorders/${selectedSaleOrderId}/user/${chosenCustomerId}/item`,
          body: payload,
        })) as { status_code?: number; message?: string };
        if ([200, 201].includes(res.status_code ?? 0)) {
          toast.success("Raw material added to order!");
          onClose();
          notifyCartUpdated();
        } else {
          toast.error(res?.message || "Could not add item to order.");
        }
      } else {
        // Default cart add flow (unchanged)
        let actionUrl = `/v1/door-management/user/${chosenCustomerId}/cart`;
        if (isCustomer && user?.user_id === chosenCustomerId) {
          actionUrl = `/v1/door-management/cart`;
        }
        const res = (await WebService.postAPI({
          action: actionUrl,
          body: payload,
        })) as { status_code?: number; message?: string };
        if ([200, 201].includes(res.status_code ?? 0)) {
          toast.success("Raw material added to cart!");
          onClose();
          notifyCartUpdated();
        } else {
          toast.error(res?.message || "Could not add item.");
        }
      }
    }
  } catch (err: any) {
    toast.error(err?.message || "Network error. Try again.");
  }
  setPosting(false);
  return;
}




    let successCount = 0;
    let errorMsg = "";
    for (const group of sizeGroups) {
      const parseNumber = (v: string) => {
        const n = parseFloat(v.replace(/[^0-9.]/g, ""));
        return isNaN(n) ? 0 : n;
      };
      const payload: any = {
        product_id: cartItem ? cartItem.product_id : (door ? door.id : undefined),
        product_type: productType,
        quantity: Number(group.quantity),
        height: parseNumber(group.height),
        width: parseNumber(group.width),
        height_margin: parseNumber(group.marginHeight),
        width_margin: parseNumber(group.marginWidth),
        mica_id: selectedMicaId,
        door_config: doubleDoor,
        is_sagwan_bidding: sagwanBidding,
        is_back_design: backDesign,
        is_back_puc: backPVC,
        wood_quality_id: Number(selectedWood),
        packaging_type_id: Number(selectedPackaging),
        description: specialRequest,
       
  product_name:
    cartItem?.product_name ||
    cartItem?.product?.product_name ||
    cartItem?.product?.name ||
    cartItem?.name ||
    door?.product_name ||
    door?.name ||
    "",
  product_description:
    cartItem?.product_description ||
    ("design_description" in (cartItem?.product || {}) && (cartItem?.product as any).design_description) ||
    cartItem?.product?.product_description ||
    cartItem?.product?.description ||
    cartItem?.description ||
    door?.product_description ||
    (("design_description" in (door || {})) ? (door as any).design_description : "") ||
    door?.description ||
    "",
};

    // Backend calculates product rate / totals — never send client money fields.
    delete payload.total_price;
    delete payload.unit_price;

    // Align payload with sale order item shape when editing an order
    if (isSaleOrderEdit && saleOrderId) {
      payload.sale_order_items_id = cartItem?.sale_order_items_id || cartItem?.cart_item_id;
      payload.sale_order_id = saleOrderId;
      payload.color = payload.color ?? cartItem?.color ?? "";
      payload.mica_id = payload.mica_id ?? cartItem?.mica_id;
      payload.is_sagwan_bidding = payload.is_sagwan_bidding ?? cartItem?.is_sagwan_bidding ?? false;
      payload.is_back_design = payload.is_back_design ?? cartItem?.is_back_design ?? false;
      payload.is_back_puc = payload.is_back_puc ?? cartItem?.is_back_puc ?? false;
      payload.wood_quality_id = payload.wood_quality_id ?? cartItem?.wood_quality_id;
      payload.packaging_type_id = payload.packaging_type_id ?? cartItem?.packaging_type_id;
      payload.discount = cartItem?.discount ?? 0;
    }

      try {
if (isEditMode && cartItem?.cart_item_id) {
  // If coming from sale order edit flow, use the dedicated API endpoint
  if (isSaleOrderEdit && saleOrderId && userId) {
    const res = (await WebService.postAPI({
      action: `/v1/door-management/saleorders/${saleOrderId}/user/${userId}/item`,
      body: payload,
    })) as { status_code?: number; message?: string };
    if ([200, 201].includes(res.status_code ?? 0)) successCount += 1;
    else errorMsg = res.message || errorMsg;
  } else {
    // Regular cart PATCH
    let actionUrl = `/v1/door-management/cart`;
    if (chosenCustomerId && chosenCustomerId !== user?.user_id) {
      actionUrl = `/v1/door-management/user/${chosenCustomerId}/cart`;
    }
    const res = (await WebService.patchAPI({
      action: actionUrl,
      body: { ...payload, cart_item_id: cartItem.cart_item_id },
    })) as { status_code?: number; message?: string };
    if ([200, 201].includes(res.status_code ?? 0)) successCount += 1;
    else errorMsg = res.message || errorMsg;
  }
}
 else if (chosenCustomerId) {
          // If an existing sale order is selected (ADMIN/accounts), post to sale order
          if (canCustomizeExistingOrders && selectedSaleOrderId) {
            const body = {
              product_id: cartItem ? cartItem.product_id : (door ? door.id : undefined),
              product_type: productType,
              quantity: Number(group.quantity),
              height: parseNumber(group.height),
              width: parseNumber(group.width),
              height_margin: parseNumber(group.marginHeight),
              width_margin: parseNumber(group.marginWidth),
              mica_id: selectedMicaId,
              door_config: doubleDoor,
              is_sagwan_bidding: sagwanBidding,
              is_back_design: backDesign,
              is_back_puc: backPVC,
              wood_quality_id: Number(selectedWood),
              packaging_type_id: Number(selectedPackaging),
              description: specialRequest,
              product_name:
                cartItem?.product_name ||
                cartItem?.product?.product_name ||
                cartItem?.product?.name ||
                cartItem?.name ||
                door?.product_name ||
                door?.name ||
                "",
              product_description:
                cartItem?.product_description ||
                ("design_description" in (cartItem?.product || {}) && (cartItem?.product as any).design_description) ||
                cartItem?.product?.product_description ||
                cartItem?.product?.description ||
                cartItem?.description ||
                door?.product_description ||
                (("design_description" in (door || {})) ? (door as any).design_description : "") ||
                door?.description ||
                "",
            };
            const res = (await WebService.postAPI({
              action: `/v1/door-management/saleorders/${selectedSaleOrderId}/user/${chosenCustomerId}/item`,
              body,
            })) as { status_code?: number; message?: string };
            if ([200, 201].includes(res.status_code ?? 0)) successCount += 1;
            else errorMsg = res.message || errorMsg;
          } else {
            // Default cart add flow
            let actionUrl = `/v1/door-management/user/${chosenCustomerId}/cart`;
            if (isCustomer && user?.user_id === chosenCustomerId) {
              actionUrl = `/v1/door-management/cart`;
            }
            const res = (await WebService.postAPI({
              action: actionUrl,
              body: payload,
            })) as { status_code?: number; message?: string };
            if ([200, 201].includes(res.status_code ?? 0)) successCount += 1;
            else errorMsg = res.message || errorMsg;
          }
        } else {
          errorMsg = "Customer not selected. ";
        }
      } catch (err: any) {
        errorMsg = err?.message || "Unknown error";
      }
    }
    setPosting(false);

    if (successCount) {
      if (isCustomer && !cartItem) {
        onClose();
        notifyCartUpdated();
        const result = await Swal.fire({
          icon: "success",
          title: "Added to cart",
          text: `${successCount} item${successCount > 1 ? "s" : ""} added. Checkout from your cart when ready.`,
          showCancelButton: true,
          confirmButtonText: "Go to Cart & Checkout",
          cancelButtonText: "Continue Shopping",
          confirmButtonColor: "#1877f2",
        });
        if (result.isConfirmed) navigate("/cart");
      } else {
        toast.success(
          cartItem
            ? "Cart item updated!"
            : `${successCount} item${successCount > 1 ? "s" : ""} added to cart!`
        );
        onClose();
        notifyCartUpdated();
      }
    } else {
      toast.error("Could not update/add cart. " + errorMsg);
    }
  };

  // --- Delete Handler (edit mode only)
  const handleDelete = async () => {
    if (!cartItem?.cart_item_id) return;

    const swalResult = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this item from the cart?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E13D22",
      cancelButtonColor: "#b7b7b7",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "swal2-popup-custom",
      },
    });

    if (swalResult.isConfirmed) {
      setDeleting(true);
      try {
        const res = (await WebService.deleteAPI({
          action: `/v1/door-management/cart/${cartItem.cart_item_id}`,
        })) as { status_code?: number; message?: string };
        if ([200, 201].includes(res.status_code ?? 0)) {
          toast.success("Item removed from cart!");
          onClose();
          notifyCartUpdated();
        } else {
          toast.error(res?.message || "Could not delete item.");
        }
      } catch (err: any) {
        toast.error(err?.message || "Network error. Try again.");
      }
      setDeleting(false);
    }
  };



  // Prepare modal image, title, description for display
const imgSrc =
  primaryProductImage(cartItem) ||
  primaryProductImage(cartItem?.product) ||
  primaryProductImage(door) ||
  "/Assets/no-pictures.png";

const title =
  cartItem?.product_name ||
  cartItem?.product?.product_name ||
  cartItem?.product?.name ||
  door?.product_name ||
  door?.name ||
  "Product";

 

const desc =
  isProduct
    ? (
        cartItem?.product_description ||
        ("design_description" in (cartItem?.product || {}) && (cartItem?.product as any).design_description) ||
        cartItem?.product?.product_description ||
        cartItem?.product?.description ||
        door?.product_description ||
        (("design_description" in (door || {})) ? (door as any).design_description : "") ||
        door?.description ||
        ""
      )
    : (
        cartItem?.product_description ||
        cartItem?.product?.description ||
        door?.product_description ||
        door?.description ||
        ""
      );




          return (
    <div className={modalStyles.variantModalOverlay}>
      <div className={modalStyles.blur}></div>
      <div className={modalStyles.popup} ref={modalRef}>
       <CrossButton onClick={onClose} className={modalStyles.closeBtn} />
        <div className={modalStyles.topRow}>
<img
  src={imgSrc}
  alt={title || "door"}
  onError={(e) => {
    (e.currentTarget as HTMLImageElement).src = "/Assets/no-pictures.png";
    (e.currentTarget as HTMLImageElement).onerror = null;
  }}
  style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 14 }}
/>

          <div className={modalStyles.doorInfo}>
            <div className={modalStyles.doorTitle}>{title}</div>
            <div className={modalStyles.doorDesc}>{desc}</div>
          </div>
        </div>
        <hr className={modalStyles.divider} />

        {/* RAW MATERIAL VARIANT UI */}
{isRawMaterial && (
  <>
    {shouldShowCustomerDropdown && (
      <div className={modalStyles.formSection} ref={custRef}>
        <label className={modalStyles.sectionLabel}>
          Select Customer <span style={{ color: "#E13D22" }}>*</span>
        </label>

        {/* Same searchable picker UI used for PRODUCT */}
        <div className={modalStyles.selectLike} onClick={() => setOpenCust(true)}>
          <input
            className={modalStyles.selectLikeInput}
            placeholder="Choose customer"
            value={openCust ? customerQuery : (selectedCustomer?.label || "")}
            onChange={(e) => {
              setOpenCust(true);
              setCustomerQuery(e.target.value);
            }}
            onFocus={() => setOpenCust(true)}
          />
          <span className={modalStyles.caret} aria-hidden="true">▾</span>
        </div>

        {openCust && (
          <div
            className={modalStyles.options}
            onMouseDown={(e) => e.preventDefault()}
          >
            {customerLoading ? (
              <div className={modalStyles.optionMuted}>Searching…</div>
            ) : customerList.length === 0 ? (
              <div className={modalStyles.optionMuted}>No results</div>
            ) : (
              customerList.map((opt) => (
                <div
                  key={opt.value}
                  className={`${modalStyles.option} ${
                    opt.value === selectedCustomer?.value ? modalStyles.optionActive : ""
                  }`}
                  onClick={() => {
                    setSelectedCustomer(opt);
                    setCustomerQuery(opt.label);
                    setOpenCust(false);
                  }}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    )}

    {/* Customise Ordered Items (raw-material, role-gated, dependent on customer, add-mode only) */}
  {isRawMaterial && canCustomizeExistingOrders && chosenCustomerId && !isEditMode && (
    <div className={modalStyles.formSection}>
      <label className={modalStyles.sectionLabel}>Customise Ordered Items</label>
      <select
        className={modalStyles.input}
        value={selectedSaleOrderId ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          setSelectedSaleOrderId(v ? Number(v) : null);
        }}
      >
        <option value="">Select sale order</option>
        {saleOrders.length === 0 ? (
          <option value="" disabled>No editable orders</option>
        ) : (
          saleOrders.map((id) => (
            <option key={id} value={id}>Order #{id}</option>
          ))
        )}
      </select>
    </div>
  )}

    {/* Extracted size (readonly) */}
    {door?.measuring_unit?.toLowerCase().includes("square feet") && (
      <div className={modalStyles.formSection}>
        {/* <label className={modalStyles.sectionLabel}>Size</label> */}
        {(extractedHeight !== null && extractedWidth !== null) ? (
          <div style={{ fontWeight: 600 }}>
            Height: {extractedHeight} | Width: {extractedWidth}
          </div>
        ) : (
          <div style={{ color: "#E13D22", fontWeight: 500 }}>
            Size information is missing in the product name!
          </div>
        )}
      </div>
    )}

    {/* Quantity: only + and - in edit mode */}
    <div className={modalStyles.formSection}>
      <label className={modalStyles.sectionLabel}>Quantity</label>
      {isEditMode ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            className={modalStyles.removeBtn}
            disabled={posting}
          >
            -
          </button>
          <span style={{ fontWeight: 600, fontSize: 17 }}>{quantity}</span>
          <button
            type="button"
            onClick={() => setQuantity((q) => q + 1)}
            className={modalStyles.addMoreBtn}
            disabled={posting}
          >
            +
          </button>
        </div>
      ) : (
        <input
          type="number"
          min={1}
          value={quantity}
          onChange={(e) => {
            let val = Number(e.target.value);
            if (isNaN(val) || val < 1) val = 1;
            setQuantity(val);
          }}
          className={modalStyles.qtyInput}
          disabled={posting}
        />
      )}
    </div>

    {/* Special Note (read-only in edit mode) */}
    <div className={modalStyles.formSection}>
      <label className={modalStyles.sectionLabel}>Special Note</label>
      <textarea
        placeholder="Write any special instruction here..."
        value={specialRequest}
        onChange={(e) => setSpecialRequest(e.target.value)}
        className={modalStyles.specialRequest}
        disabled={isEditMode && isRawMaterial}
      />
    </div>

    <button
      className={modalStyles.confirmBtn}
      type="button"
      onClick={handleConfirm}
      disabled={
        posting ||
        (shouldShowCustomerDropdown && !chosenCustomerId) ||
        (door?.measuring_unit?.toLowerCase().includes("square feet") &&
          (extractedHeight === null || extractedWidth === null))
      }
    >
      {posting
        ? isEditMode
          ? "Updating..."
          : "Adding..."
        : isEditMode
          ? "Update Raw Material"
          : "Add Raw Material"}
    </button>

    {isEditMode && (
      <button
        className={modalStyles.removeBtn}
        type="button"
        style={{ marginLeft: 12, marginTop: 12, background: "#fff0f0" }}
        onClick={handleDelete}
        disabled={deleting}
      >
        {deleting ? "Deleting..." : "Delete"}
      </button>
    )}
  </>
)}

{/* PRODUCT VARIANT UI */}
{isProduct && (
  <>
  {shouldShowCustomerDropdown && (
  <div className={modalStyles.formSection} ref={custRef}>
    <label className={modalStyles.sectionLabel}>
      Select Customer <span style={{ color: "#E13D22" }}>*</span>
    </label>

    {/* looks like a <select>, but searchable */}
    <div className={modalStyles.selectLike} onClick={() => setOpenCust(true)}>
      <input
        className={modalStyles.selectLikeInput}
        placeholder="Choose customer"
        value={openCust ? customerQuery : (selectedCustomer?.label || "")}
        onChange={(e) => {
          setOpenCust(true);
          setCustomerQuery(e.target.value);
        }}
        onFocus={() => setOpenCust(true)}
      />
      <span className={modalStyles.caret} aria-hidden>▾</span>
    </div>

    {openCust && (
      <div className={modalStyles.options} onMouseDown={(e) => e.preventDefault()}>
        {customerLoading ? (
          <div className={modalStyles.optionMuted}>Searching…</div>
        ) : customerList.length === 0 ? (
          <div className={modalStyles.optionMuted}>No results</div>
        ) : (
          customerList.map((opt) => (
            <div
              key={opt.value}
              className={`${modalStyles.option} ${
                opt.value === selectedCustomer?.value ? modalStyles.optionActive : ""
              }`}
              onClick={() => {
                setSelectedCustomer(opt);
                setCustomerQuery(opt.label);
                setOpenCust(false);
              }}
            >
              {opt.label}
            </div>
          ))
        )}
      </div>
    )}
  </div>
)}

  {/* Customise Ordered Items (product-only, role-gated, dependent on customer, add-mode only) */}
  {isProduct && canCustomizeExistingOrders && chosenCustomerId && !isEditMode && (
    <div className={modalStyles.formSection}>
      <label className={modalStyles.sectionLabel}>Customise Ordered Items</label>
      <select
        className={modalStyles.input}
        value={selectedSaleOrderId ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          setSelectedSaleOrderId(v ? Number(v) : null);
        }}
      >
        <option value="">Select sale order</option>
        {saleOrders.length === 0 ? (
          <option value="" disabled>No editable orders</option>
        ) : (
          saleOrders.map((id) => (
            <option key={id} value={id}>Order #{id}</option>
          ))
        )}
      </select>
    </div>
  )}
    

    {/* 1) Wood Type FIRST */}
    <div className={modalStyles.formSection}>
      <div className={modalStyles.sectionHeader}>
        <label className={modalStyles.sectionLabel}>
          Select Wood Type <span style={{ color: "#E13D22" }}>*</span>
        </label>
        {isAdminOrEmployee && (
          <button
            className={modalStyles.addMoreBtnFixed}
            type="button"
            onClick={handleAddWoodQuality}
            disabled={posting}
          >
            + Add wood quality
          </button>
        )}
      </div>
      <select
        value={selectedWood}
        onChange={(e) => {
          setSelectedWood(e.target.value);
          setWoodTypeError("");
        }}
        className={woodTypeError ? modalStyles.inputError : ""}
      >
        <option value="">Choose Wood Type</option>
        {woodTypes.map((w) => (
          <option key={w.id} value={w.id.toString()}>
            {w.name}
          </option>
        ))}
      </select>
      {woodTypeError && (
        <div className={modalStyles.errorMsg}>{woodTypeError}</div>
      )}
      {/* tiny helper note for Hardwood */}
      {isHardwood && (
        <div className={modalStyles.summaryRow}>
          Hardwood selected: Height up to 84", Width up to 44".
        </div>
      )}
    </div>

    {/* 2) Size/Margins/Qty AFTER wood type, with dynamic limits for Hardwood */}
<div className={modalStyles.formSection}>
  {/* Header with fixed Add more on the right */}
  <div className={modalStyles.sectionHeader}>
    <label className={modalStyles.sectionLabel}>Set Door Size, Margin & Quantity</label>

    {!isEditMode && (
      <button
        className={modalStyles.addMoreBtnFixed}
        type="button"
        onClick={() => setSizeGroups([...sizeGroups, initialGroup()])}
        disabled={posting}
      >
        Add more +
      </button>
    )}
  </div>

  {sizeGroups.map((group, idx) => (
    <div key={idx} className={modalStyles.sizeRow}>
      {/* Height */}
      <div>
        <label className={modalStyles.subLabel}>Height</label>
        <select
          value={group.height}
          onChange={(e) => {
            const arr = [...sizeGroups];
            arr[idx].height = e.target.value;
            setSizeGroups(arr);
          }}
        >
          {Array.from({ length: heightMax - HEIGHT_MIN + 1 }, (_, i) => HEIGHT_MIN + i).map(
            (inch) => (
              <option key={inch} value={`${inch}in`}>{inch} in</option>
            )
          )}
        </select>
      </div>

      {/* Height Margin */}
      <div>
        <label className={modalStyles.subLabel}>Height Margin</label>
        <select
          value={group.marginHeight}
          onChange={(e) => {
            const arr = [...sizeGroups];
            arr[idx].marginHeight = e.target.value;
            setSizeGroups(arr);
          }}
        >
          {["0", "0.25", "0.5", "0.75"].map((m) => (
            <option key={m} value={`${m}in`}>{m} in</option>
          ))}
        </select>
      </div>

      {/* Width */}
      <div>
        <label className={modalStyles.subLabel}>Width</label>
        <select
          value={group.width}
          onChange={(e) => {
            const arr = [...sizeGroups];
            arr[idx].width = e.target.value;
            setSizeGroups(arr);
          }}
        >
          {Array.from({ length: widthMax - WIDTH_MIN + 1 }, (_, i) => WIDTH_MIN + i).map(
            (inch) => (
              <option key={inch} value={`${inch}in`}>{inch} in</option>
            )
          )}
        </select>
      </div>

      {/* Width Margin */}
      <div>
        <label className={modalStyles.subLabel}>Width Margin</label>
        <select
          value={group.marginWidth}
          onChange={(e) => {
            const arr = [...sizeGroups];
            arr[idx].marginWidth = e.target.value;
            setSizeGroups(arr);
          }}
        >
          {["0", "0.25", "0.5", "0.75"].map((m) => (
            <option key={m} value={`${m}in`}>{m} in</option>
          ))}
        </select>
      </div>

      {/* Qty */}
      <div>
        <label className={modalStyles.subLabel}>Qty</label>
        <input
          type="number"
          min={1}
          value={group.quantity}
          onChange={(e) => {
            const arr = [...sizeGroups];
            arr[idx].quantity = Number(e.target.value);
            setSizeGroups(arr);
          }}
          className={modalStyles.qtyInput}
          disabled={posting}
        />
      </div>

      {/* Row remove stays */}
      {sizeGroups.length > 1 && (
        <button
          type="button"
          className={modalStyles.removeBtn}
          onClick={() => {
            if (sizeGroups.length > 1)
              setSizeGroups(sizeGroups.filter((_, i) => i !== idx));
          }}
          style={{ marginLeft: 6 }}
        >
          ×
        </button>
      )}
    </div>
  ))}
</div>

<div className={modalStyles.formSection}>
  <label className={modalStyles.sectionLabel}>Select Mica</label>

{/* --- PREMIUM MICA --- */}
{micaPremium.length > 0 && (
  <>
    <div className={modalStyles.micaSectionLabel}>Premium</div>
    <div className={modalStyles.micaRow}>
      {micaPremium.map((mica) => {
        const isHovered = hoveredMica?.mica_id === mica.mica_id;
        return (
          <div
            key={mica.mica_id}
            title={mica.name}
            className={
              [
                modalStyles.micaCircle,
                selectedMicaId === mica.mica_id ? modalStyles.selectedMica : "",
                isHovered ? modalStyles.micaCircleExpanded : ""
              ].join(" ")
            }
            onClick={() => setSelectedMicaId(mica.mica_id)}
            onMouseEnter={() => setHoveredMica(mica)}
            onMouseLeave={() => setHoveredMica(null)}
          >
            {mica.image_url && (
              <img
                src={mediaUrl(mica.image_url)}
                alt={mica.name}
                className={modalStyles.micaImg}
              />
            )}
            {isHovered && (
              <div className={modalStyles.micaName}>{mica.name}</div>
            )}
          </div>
        );
      })}
    </div>
  </>
)}

{/* --- STANDARD MICA --- */}
{micaStandard.length > 0 && (
  <>
    <div className={modalStyles.micaSectionLabel}>Standard</div>
    <div className={modalStyles.micaRow}>
      
     
     
      {micaStandard.map((mica,idx) => {
        const isHovered = hoveredMica?.mica_id === mica.mica_id;
        return (
          <div
            key={mica.mica_id}
            title={mica.name}
            className={
              [
                modalStyles.micaCircle,
                selectedMicaId === mica.mica_id ? modalStyles.selectedMica : "",
                isHovered ? modalStyles.micaCircleExpanded : ""
              ].join(" ")
            }
            onClick={() => setSelectedMicaId(mica.mica_id)}
            onMouseEnter={() => setHoveredMica(mica)}
            onMouseLeave={() => setHoveredMica(null)}
          >
            {mica.image_url && (
              <img
                src={mediaUrl(mica.image_url)}
                alt={mica.name}
                className={modalStyles.micaImg}
              />
            )}
            {isHovered && (
              <div className={modalStyles.micaName}>{mica.name}</div>
            )}
          </div>
        );
      })}
    </div>
  </>
)}
</div>
            <div className={modalStyles.formSection}>
              <label className={modalStyles.sectionLabel}>
                Door Configuration
              </label>
              <div className={modalStyles.configRow}>
                <button
                  type="button"
                  className={doubleDoor ? modalStyles.configActive : ""}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDoubleDoor((d) => !d);
                  }}
                >
                  <img
                    src={doubleDoorIcon}
                    alt="Double Door"
                    className={modalStyles.doorIcon}
                  />
                  Double Door
                </button>
                <button
                  type="button"
                  className={sagwanBidding ? modalStyles.configActive : ""}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSagwanBidding((d) => !d);
                  }}
                >
                  <img
                    src={singleDoorIcon}
                    alt="Sagwan Bidding"
                    className={modalStyles.doorIcon}
                  />
                  Sagwan Bidding
                </button>
              </div>
            </div>
            <div className={modalStyles.formSection}>
              <label className={modalStyles.sectionLabel}>Design Sides</label>
              <div className={modalStyles.designRow}>
                <button
                  type="button"
                  className={backDesign ? modalStyles.designSelected : ""}
                  onClick={(e) => {
                    e.stopPropagation();
                    setBackDesign((s) => !s);
                  }}
                >
                  <img
                    src={doubleDoorIcon}
                    alt="Back Design"
                    className={modalStyles.designIcon}
                  />
                  Back Design
                </button>
                <button
                  type="button"
                  className={backPVC ? modalStyles.designSelected : ""}
                  onClick={(e) => {
                    e.stopPropagation();
                    setBackPVC((s) => !s);
                  }}
                >
                  <img
                    src={singleDoorIcon}
                    alt="Back PVC"
                    className={modalStyles.designIcon}
                  />
                  Back PVC
                </button>
              </div>
            </div>
          <div className={modalStyles.formSection}>
  <label className={modalStyles.sectionLabel}>Choose Packaging</label>
  <select
    value={selectedPackaging}
    onChange={(e) => {
      setSelectedPackaging(e.target.value);
      setPackagingError(""); 
    }}
    disabled={packagingOptions.length === 0}
    className={packagingError ? modalStyles.inputError : ""}
  >
    <option value="">Choose Packaging</option>
    {packagingOptions.map((opt) => {
      const pricePerPiece = Number(opt["price/sqft"] ?? opt.price ?? 0);
      return (
      <option key={opt.id} value={opt.id.toString()}>
        {opt.name} - ₹{pricePerPiece}/piece
      </option>
      );
    })}
  </select>
  {packagingError && (
    <div className={modalStyles.errorMsg}>{packagingError}</div>
  )}
</div>

            <div className={modalStyles.formSection}>
              <label className={modalStyles.sectionLabel}>
                Add Special Request
              </label>
              <textarea
                placeholder="Write your customisation request here..."
                value={specialRequest}
                onChange={(e) => setSpecialRequest(e.target.value)}
                className={modalStyles.specialRequest}
              />
            </div>
            <button
              className={modalStyles.confirmBtn}
              type="button"
              onClick={handleConfirm}
              disabled={
                posting ||
                (isProduct && !canAddToCart(door)) ||
                (shouldShowCustomerDropdown && !chosenCustomerId) 
                // !selectedWood ||
                // !selectedPackaging
              }
            >
              {posting
                ? isEditMode
                  ? "Updating..."
                  : "Adding..."
                : isEditMode
                  ? "Update Product"
                  : "Confirm variant"} {/* add product */}
            </button>
            {isEditMode && (
              <button
                className={modalStyles.removeBtn}
                type="button"
                style={{ marginLeft: 12, marginTop: 12, background: "#fff0f0" }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChooseVariantModal;

